import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import styles from './VideoBanner-tw-styles';
import Typography from '../../Generic/Typography';
import getWidth from '../../../utils/gridWidth';
import { getVideoSrcSetW, getCdnImage } from '../../../utils/ImageManager';
import i18nMessages from '../../../constants/messages/pt-BR';
import DOMPurify from 'isomorphic-dompurify';

class VideoBanner extends Component {
  constructor(props) {
    super(props);
    this.state = {
      videoUrl: '',
      image: '',
      isIOS: false,
      isMobile: false,
      mobileUserPaused: false,
    };

    this.userPausedVideo = false;
    this.mobileListenersAdded = false;
    this.blockUnexpectedPlay = false;

    this.videoRef = React.createRef();
    this.playButtonRef = React.createRef();
    this.pauseButtonRef = React.createRef();
  }

  getVideoElement = () => {
    if (this.videoRef.current) {
      return this.videoRef.current;
    }

    if (typeof document !== 'undefined') {
      return document.getElementById('my-player-banner');
    }

    return null;
  };

  updateMobileButtons = (isPlaying) => {
    const playButton = this.playButtonRef.current;
    const pauseButton = this.pauseButtonRef.current;

    if (!playButton || !pauseButton) return;

    if (isPlaying) {
      pauseButton.style.display = 'block';
      pauseButton.removeAttribute('aria-hidden');
      pauseButton.setAttribute('tabindex', '0');

      playButton.style.display = 'none';
      playButton.setAttribute('aria-hidden', 'true');
      playButton.setAttribute('tabindex', '-1');
    } else {
      playButton.style.display = 'block';
      playButton.removeAttribute('aria-hidden');
      playButton.setAttribute('tabindex', '0');

      pauseButton.style.display = 'none';
      pauseButton.setAttribute('aria-hidden', 'true');
      pauseButton.setAttribute('tabindex', '-1');
    }
  };

  playVideo = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const video = this.getVideoElement();

    if (video) {
      this.userPausedVideo = false;

      if (this.state.isMobile) {
        video.autoplay = true;
        video.setAttribute('autoplay', '');
      }

      this.setState({ mobileUserPaused: false });

      const playPromise = video.play();

      if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(() => {
            this.updateMobileButtons(true);
          })
          .catch(() => {
            this.updateMobileButtons(false);
          });
      } else {
        this.updateMobileButtons(true);
      }
    }
  };

  pauseVideo = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const video = this.getVideoElement();

    if (video) {
      this.userPausedVideo = true;

      if (this.state.isMobile) {
        video.autoplay = false;
        video.removeAttribute('autoplay');
      }

      video.pause();

      this.setState({ mobileUserPaused: true }, () => {
        this.updateMobileButtons(false);
      });
    }
  };

  handlePlay = () => {
    const video = this.getVideoElement();

    if (
      this.state.isMobile &&
      this.userPausedVideo &&
      video &&
      !this.blockUnexpectedPlay
    ) {
      this.blockUnexpectedPlay = true;

      video.autoplay = false;
      video.removeAttribute('autoplay');

      window.requestAnimationFrame(() => {
        if (this.userPausedVideo && video && !video.paused) {
          video.pause();
        }

        this.updateMobileButtons(false);
        this.blockUnexpectedPlay = false;
      });

      return;
    }

    if (this.state.isMobile) {
      this.updateMobileButtons(true);
    }
  };

  handlePause = () => {
    if (this.state.isMobile) {
      this.updateMobileButtons(false);
    }
  };

  setupMobileVideoListeners = () => {
    if (!this.state.isMobile || this.mobileListenersAdded) return;

    const video = this.getVideoElement();

    if (video) {
      this.mobileListenersAdded = true;

      video.addEventListener('play', this.handlePlay);
      video.addEventListener('pause', this.handlePause);

      if (video.paused) {
        this.updateMobileButtons(false);
      } else {
        this.updateMobileButtons(true);
      }
    }
  };

  componentDidMount() {
    const { data } = this.props;
    let videoURL, desktopImage;
    const { desktopVideo, mobileVideo, imageSet } = data;

    if (typeof window !== 'undefined') {
      const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

      const isMobile =
        window.innerWidth <= 767 ||
        /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

      this.setState({ isIOS, isMobile });

      window.addEventListener(i18nMessages.resize, this.determineWidth);

      if (window.innerWidth > 767) {
        videoURL = getVideoSrcSetW(desktopVideo.url, window.innerWidth);
        desktopImage = getCdnImage(
          imageSet?.desktopImage?.url,
          window.innerWidth
        );

        this.setState(
          {
            videoUrl: videoURL,
            image: desktopImage,
          },
          () => {
            if (isMobile) {
              setTimeout(this.setupMobileVideoListeners, 300);
            }
          }
        );
      } else {
        videoURL = getVideoSrcSetW(mobileVideo.url, 480);
        desktopImage = getCdnImage(imageSet?.smartphoneImage?.url);

        this.setState(
          {
            videoUrl: videoURL,
            image: desktopImage,
          },
          () => {
            if (isMobile) {
              setTimeout(this.setupMobileVideoListeners, 300);
            }
          }
        );
      }
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener(i18nMessages.resize, this.determineWidth);

      const video = this.getVideoElement();

      if (video) {
        video.removeEventListener('play', this.handlePlay);
        video.removeEventListener('pause', this.handlePause);
      }
    }
  }

  render() {
    const { data } = this.props;
    const { videoUrl, image, isIOS, isMobile, mobileUserPaused } = this.state;

    let contentType = data.styles && data.styles.trim();

    if (styles[contentType] === undefined) {
      contentType = i18nMessages.VideoBanner;
    }

    const widthClass = getWidth(data.componentWidth);
    const desktopPoster = getCdnImage(data?.imageSet?.desktopImage?.url);
    const posterImage = image !== '' ? image : desktopPoster;

    const mobileButtonStyle = {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '80px',
      height: '80px',
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      zIndex: 10,
      outline: 'none',
      WebkitTapHighlightColor: 'transparent',
    };

    return (
      <>
        <style
          dangerouslySetInnerHTML={{
            __html: `
              #my-player-banner::-webkit-media-controls-timeline {
                display: none !important;
              }
              #my-player-banner::-webkit-media-controls-current-time-display {
                display: none !important;
              }
              #my-player-banner::-webkit-media-controls-time-remaining-display {
                display: none !important;
              }
              #my-player-banner::-webkit-media-controls-mute-button {
                display: none !important;
              }
              #my-player-banner::-webkit-media-controls-volume-slider {
                display: none !important;
              }
              #my-player-banner::-webkit-media-controls-fullscreen-button {
                display: none !important;
              }
              #my-player-banner::-webkit-media-controls-overflow-button {
                display: none !important;
                opacity: 0 !important;
                width: 0 !important;
                height: 0 !important;
                pointer-events: none !important;
              }
              video#my-player-banner::-webkit-media-controls-overflow-button {
                display: none !important;
              }
              #my-player-banner::-webkit-media-controls-toggle-closed-captions-button {
                display: none !important;
              }
            `,
          }}
        />

        <div className={classNames(styles[contentType].mainWrapper, widthClass)}>
          <a href={data.targetUrl}>
            {data?.title ||
              (data?.text && (
                <div className={classNames(styles[contentType].textWrapper)}>
                  <Typography
                    key={data.sys.id}
                    className={classNames(styles[contentType].headingStyle)}
                    component="div"
                    dangerouslySetInnerHTML={
                      data.title && {
                        __html: DOMPurify.sanitize(data.title, {
                          ADD_TAGS: ['iframe'],
                          ADD_ATTR: [
                            'allow',
                            'allowfullscreen',
                            'frameborder',
                            'scrolling',
                            'onload',
                          ],
                        }),
                      }
                    }
                  />

                  <Typography
                    key={data.sys.id}
                    className={classNames(styles[contentType].subText)}
                    component="div"
                    dangerouslySetInnerHTML={
                      data.text && {
                        __html: DOMPurify.sanitize(data.text, {
                          ADD_TAGS: ['iframe'],
                          ADD_ATTR: [
                            'allow',
                            'allowfullscreen',
                            'frameborder',
                            'scrolling',
                            'onload',
                          ],
                        }),
                      }
                    }
                  />
                </div>
              ))}

            <div id="video-description" className="sr-only" tabIndex="0">
              A linha de produtos Pantene Bond Repair, flutuando em frente a
              átomos, simboliza a reparação profunda em nível molecular dos
              danos capilares.
            </div>

            <div style={{ position: 'relative' }}>
              {isMobile ? (
                <div
                  id="follower"
                  className={classNames(styles[contentType].previewVideo)}
                >
                  <video
                    ref={this.videoRef}
                    id="my-player-banner"
                    className="vjs-tech min-h-full"
                    preload="metadata"
                    playsInline
                    poster={posterImage}
                    autoPlay={!mobileUserPaused}
                    muted
                    loop
                    controls
                    controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
                    disablePictureInPicture
                    tabIndex={isIOS ? '-1' : undefined}
                    aria-hidden={isIOS ? 'true' : undefined}
                    aria-describedby="video-description"
                  >
                    <source src={videoUrl} type="video/mp4" />
                  </video>
                </div>
              ) : (
                <div
                  id="follower"
                  className={classNames(styles[contentType].previewVideo)}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      `<video
                        id="my-player-banner"
                        className="vjs-tech min-h-full"
                        preload="metadata"
                        playsInline
                        poster="${posterImage}"
                        autoPlay
                        muted
                        loop
                        controls
                        controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
                        disablePictureInPicture
                        aria-describedby="video-description"
                      >
                        <source src="${videoUrl}" type="video/mp4" />
                      </video>`,
                      {
                        ADD_TAGS: ['iframe'],
                        ADD_ATTR: [
                          'allow',
                          'allowfullscreen',
                          'frameborder',
                          'scrolling',
                          'onload',
                        ],
                      }
                    ),
                  }}
                />
              )}

              {isMobile && (
                <>
                  <button
                    ref={this.pauseButtonRef}
                    onClick={this.pauseVideo}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        this.pauseVideo(e);
                      }
                    }}
                    style={mobileButtonStyle}
                    aria-label="Pause video"
                    type="button"
                  >
                    <span className="sr-only">Pause video</span>
                  </button>

                  <button
                    ref={this.playButtonRef}
                    onClick={this.playVideo}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        this.playVideo(e);
                      }
                    }}
                    style={{
                      ...mobileButtonStyle,
                      display: 'none',
                    }}
                    aria-label="Play video"
                    aria-hidden="true"
                    tabIndex="-1"
                    type="button"
                  >
                    <span className="sr-only">Play video</span>
                  </button>
                </>
              )}
            </div>
          </a>
        </div>
      </>
    );
  }
}

VideoBanner.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object]),
};

VideoBanner.defaultProps = {
  data: null,
};

export default VideoBanner;